import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { StatusBadge, AttemptBadge } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Send, FileDown, CheckCircle2, ArrowLeft, Mic, Volume2, VolumeX, Loader2 } from "lucide-react";

export default function ChatSession() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const [voiceMode, setVoiceMode] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [audioLoading, setAudioLoading] = useState(false);
  const recognitionRef = useRef(null);

  const toggleListening = () => {
    if (isListening && recognitionRef.current) {
      recognitionRef.current.stop();
      return;
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      toast.error("Tu navegador no soporta dictado por voz");
      return;
    }
    const recognition = new SpeechRecognition();
    recognitionRef.current = recognition;
    recognition.lang = 'es-MX';
    recognition.interimResults = false;
    recognition.onstart = () => setIsListening(true);
    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setInput((prev) => prev + (prev ? " " : "") + transcript);
    };
    recognition.onend = () => setIsListening(false);
    recognition.onerror = () => setIsListening(false);
    recognition.start();
  };

  const playTTS = async (text) => {
    setAudioLoading(true);
    try {
      const res = await api.post("/chat/tts", { text, voice: "alloy" }, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const audio = new Audio(url);
      audio.play();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error generando audio (¿Falta API Key OpenAI?)");
    } finally {
      setAudioLoading(false);
    }
  };


  const load = async () => {
    try {
      const { data } = await api.get(`/sessions/${id}`);
      setSession(data);
      setMessages(data.messages || []);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
      navigate("/alumno");
    }
  };

  useEffect(() => { load(); }, [id]);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, sending]);

  const send = async () => {
    const content = input.trim();
    if (!content || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "student", content, timestamp: new Date().toISOString(), _local: true }]);
    setSending(true);
    try {
      const { data } = await api.post(`/sessions/${id}/message`, { content });
      setMessages((m) => [...m, { role: "patient", content: data.reply, timestamp: new Date().toISOString(), ai: data.ai }]);
      if (voiceMode) playTTS(data.reply);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const endSession = async () => {
    try {
      await api.post(`/sessions/${id}/end`);
      toast.success("Sesión finalizada");
      load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/sessions/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${session.public_id}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF descargado");
      load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  if (!session) return <AppShell><div className="p-10 text-center text-sm text-muted-foreground">Cargando...</div></AppShell>;

  const finished = session.status === "finished";

  return (
    <AppShell>
      <button onClick={() => navigate("/alumno")} className="mb-4 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="back-alumno">
        <ArrowLeft className="h-4 w-4" /> Volver al panel
      </button>

      <div className="grid gap-6 lg:grid-cols-[1fr_300px]">
        {/* Chat */}
        <div className="flex h-[70vh] flex-col overflow-hidden rounded-xl border border-border bg-card">
          <div className="flex items-center justify-between border-b border-border px-5 py-3">
            <div className="flex items-center gap-3">
              <Avatar className="h-9 w-9">
                <AvatarImage src={session.patient_avatar} alt={session.patient_name} />
                <AvatarFallback>{session.patient_name?.[0]}</AvatarFallback>
              </Avatar>
              <div>
                <p className="text-sm font-semibold">{session.patient_name}</p>
                <p className="text-xs text-muted-foreground">{session.exercise_title}</p>
              </div>
            </div>
                        <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" className="h-8 gap-1.5" onClick={() => setVoiceMode(!voiceMode)}>
                {voiceMode ? (
                  <><Volume2 className="h-4 w-4 text-emerald-600" /><span className="text-emerald-600 text-xs font-medium">Voz Activa</span></>
                ) : (
                  <><VolumeX className="h-4 w-4 text-muted-foreground" /><span className="text-xs text-muted-foreground">Voz Inactiva</span></>
                )}
              </Button>
              <StatusBadge status={session.status} />
            </div>
          </div>

          <div ref={scrollRef} className="sepp-scroll flex-1 space-y-4 overflow-y-auto p-5">
            {messages.length === 0 && (
              <p className="mt-8 text-center text-sm text-muted-foreground">
                Inicia la entrevista saludando al paciente y presentándote.
              </p>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex sepp-fade ${m.role === "student" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[78%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                  m.role === "student"
                    ? "rounded-br-sm bg-primary text-primary-foreground"
                    : "rounded-bl-sm border border-border bg-secondary text-secondary-foreground"}`}>
                  <p className={`mb-0.5 text-[10px] font-medium uppercase tracking-wider ${m.role === "student" ? "text-teal-100" : "text-violet-600"}`}>
                    {m.role === "student" ? "Tú (terapeuta)" : `Paciente${m.ai === false ? " · demo" : ""}`}
                  </p>
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="rounded-2xl rounded-bl-sm border border-border bg-secondary px-4 py-3">
                  <span className="sepp-dot inline-block h-2 w-2 rounded-full bg-violet-500" style={{ animationDelay: "0s" }} />
                  <span className="sepp-dot mx-1 inline-block h-2 w-2 rounded-full bg-violet-500" style={{ animationDelay: "0.2s" }} />
                  <span className="sepp-dot inline-block h-2 w-2 rounded-full bg-violet-500" style={{ animationDelay: "0.4s" }} />
                </div>
              </div>
            )}
          </div>

          {!finished ? (
            <div className="border-t border-border p-3">
              <div className="flex items-end gap-2 relative">
                <Button 
                  variant={isListening ? "destructive" : "secondary"} 
                  size="icon" 
                  onClick={toggleListening} 
                  title="Dictar por voz"
                  className={`shrink-0 ${isListening ? 'animate-pulse' : ''}`}
                >
                  <Mic className="h-4 w-4" />
                </Button>
                <Textarea
                  data-testid="chat-message-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder={isListening ? "Escuchando..." : "Escribe tu intervención o dicta por voz..."}
                  className="max-h-28 min-h-[44px] resize-none"
                />
                <Button size="icon" disabled={sending || !input.trim()} onClick={send} data-testid="chat-send-button" className="shrink-0 relative">
                  {audioLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          ) : (
            <div className="border-t border-border bg-emerald-50 px-5 py-3 text-center text-sm text-emerald-700">
              <CheckCircle2 className="mr-1 inline h-4 w-4" /> Sesión finalizada
            </div>
          )}
        </div>

        {/* Side panel */}
        <aside className="space-y-4">
          <div className="rounded-xl border border-border bg-card p-5">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Estado de la sesión</p>
            <p className="mt-2 font-mono text-sm text-primary">{session.public_id}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <StatusBadge status={session.status} />
              <AttemptBadge n={session.attempt_number} />
            </div>
            <div className="mt-4 space-y-2 text-xs text-muted-foreground">
              <p>Mensajes: {messages.filter((m) => m.role !== "system").length}</p>
              <p>PIN de verificación: <span className="font-mono">{finished ? "en PDF" : "al finalizar"}</span></p>
            </div>
          </div>

          <div className="space-y-2">
            {!finished ? (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="w-full" data-testid="chat-end-session-button">
                    Finalizar sesión
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>¿Finalizar la sesión?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Una vez finalizada no podrás enviar más mensajes, pero podrás descargar el PDF con QR y PIN.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction onClick={endSession} data-testid="confirm-end-session">Finalizar</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            ) : (
              <Button className="w-full" onClick={downloadPdf} data-testid="chat-export-pdf-button">
                <FileDown className="mr-2 h-4 w-4" />Descargar PDF
              </Button>
            )}
          </div>
        </aside>
      </div>
    </AppShell>
  );
}
